<script>
import {
  CdrText,
  CdrLink,
  CdrBreadcrumb,
  CdrButton,
  IconStorefront,
  IconChat,
  IconMail,
  IconTelephone,
} from '@rei/cedar';
import { contactUsSections } from '../../common/contactUsSections';
import HoursComponent from './HoursComponent.vue';
import { sendClickAnalytics } from '../../utils';
import tags from '../../utils/globalTags';
import { emailResponseTimeDefaultVal } from '../../common/shared';

export default {
  name: 'CUContentComponent',
  components: {
    HoursComponent,
    CdrText,
    CdrLink,
    CdrBreadcrumb,
    CdrButton,
    IconStorefront,
    IconChat,
    IconMail,
    IconTelephone,
  },
  props: {
    title: { type: String, default: 'Send Us an Email' },
    className: { type: String, default: '' },
    id: { type: String, default: '' },
    retailStoreHoursEvents: { type: Array, default() { return []; } },
    emailResponseTime: { type: String, default: emailResponseTimeDefaultVal },
    helpCenterNumbers: { type: Object, default() { return {}; } },
    isContactUsPage: { type: Boolean, default: false },
  },
  data() {
    return {
      breadCrumbItems: [
        { item: { url: '/help', name: 'Help Center' } },
        { item: { url: '', name: 'Contact us' } },
      ],
      hoursModel: null,
      contactUsSections,
    };
  },
  created() {
    const { retailStoreHoursEvents } = this;
    this.hoursModel = { retailStoreHoursEvents };
  },
  mounted() {},
  methods: {
    metricHandler(source = 'link') {
      const clickData = {
        pageName: this.pageName,
        linkName: `${tags.HELP}_${source}`,
      };
      sendClickAnalytics(clickData);
    },
    sectionClick(sectionVal) {
      switch (sectionVal.section) {
        case 'Chat':
          if (window.Gladly) {
            window.Gladly.show();
          }
          this.metricHandler(sectionVal.adobeTag);
          break;
        case 'Email':
          this.metricHandler(sectionVal.adobeTag);
          window.location = sectionVal.redirectUrl;
          break;
        default:
          break;
      }
    },
  },
};
</script>
<template>
  <div
    :id="id"
    :class="className"
  >
    <cdr-text
      v-if="!isContactUsPage"
      tag="h2"
      tabindex="0"
    >
      <span class="contact-us-header-title">
        Contact Us
      </span>
    </cdr-text>
    <hours-component
      :hours-model="hoursModel"
    />
    <div class="contact-us-row">
      <div
        v-for="(section, index) in contactUsSections"
        :key="index"
        class="contact-us-column"
      >
        <div tabindex="0">
          <div
            v-if="!!section.recommended"
            class="contact-us-recommended"
          >
            <cdr-text>
              <span class="contact-us-recommended-text">
                Recommended
              </span>
            </cdr-text>
          </div>
          <div
            class="contact-us-sectionValues"
            :class="index === 0 ? 'contact-us-sectionValues-first' : ''"
          >
            <div class="contact-us-secVal">
              <div class="contact-us-secVal-it">
                <component
                  :is="section.icon"
                  class="contact-us-sec-icon"
                />
                <cdr-text>
                  <span class="contact-us-sec-heading">
                    {{ section.section }}
                  </span>
                </cdr-text>
              </div>
              <cdr-text>
                <span class="contact-us-sec-side-text">
                  {{ section.sideText }}
                </span>
              </cdr-text>
            </div>
            <cdr-text>
              <span class="contact-us-sec-body">
                {{ section.subText }}
              </span>
            </cdr-text>
            <div class="contact-us-sec-button">
              <cdr-button
                modifier="secondary"
                @click="sectionClick(section)"
              >
                {{ section.buttonContent }}
              </cdr-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="contact-us-email-content"
    >
      <cdr-text
        tag="h3"
        tabindex="0">
        <span class="contact-us-footer-title">
          Email Us:
        </span>
      </cdr-text>
      <cdr-text
        tabindex="0"
      >
        <span class="contact-us-sec-body-footer">
          For non-urgent issues and questions, feel free to <cdr-link href="/help/contact-us" @click="metricHandler('card_email')">email us</cdr-link> anytime.
        </span>
      </cdr-text>
    </div>
    <div
      class="contact-us-phone-content"
    >
      <cdr-text
        tag="h3"
        tabindex="0"
      >
        <span class="contact-us-footer-title">
          Call Us:
        </span>
      </cdr-text>
      <cdr-text
        tabindex="0"
      >
        <span class="contact-us-sec-body-footer">
          For time-sensitive issues like canceling an order or help with payment.
        </span>
      </cdr-text>
      <cdr-text
        tabindex="0"
      >
        <span class="contact-us-sec-body-footer">
          U.S. callers: <cdr-link
            :href="`tel:+${helpCenterNumbers.usCallers.replaceAll('-', '')}`"
            @click="metricHandler( 'link_us-phone')"
          >
            {{ helpCenterNumbers.usCallers }}
          </cdr-link>
        </span>
      </cdr-text>
      <cdr-text
        tabindex="0"
      >
        <span class="contact-us-sec-body-footer">
          International callers: <cdr-link
            :href="`tel:+${helpCenterNumbers.internationalCallers.replaceAll('-', '')}`"
            @click="metricHandler( 'link_international-phone')"
          >
            {{ helpCenterNumbers.internationalCallers }}
          </cdr-link>
        </span>
      </cdr-text>
    </div>
    <div
      class="contact-us-store-content"
    >
      <cdr-text
        tag="h3"
        tabindex="0"
      >
        <span class="contact-us-footer-title">
          In-store support:
        </span>
      </cdr-text>
      <cdr-link
        modifier="standalone"
        class="contact-us-sec-body-footer"
        href="/stores/map"
        @click="metricHandler('card_stores')"
      >
        Find a store near you
      </cdr-link>
    </div>
  </div>
</template>
<style lang="scss">
  @import 'src/main/style/contactUs.scss';
</style>
